import React, { useState } from 'react';
import { CheckCircleIcon, AlertCircleIcon, HelpCircleIcon, LinkIcon, SearchIcon, CheckIcon } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
interface TicketData {
  id: number;
  ticketNumber: string;
  paxName: string;
  airlineCode: string;
  issueDate: string;
  airFare: number;
  tax: number;
  totalCost: number;
  reconciled: boolean;
}
interface BillingData {
  id: number;
  ticketNumber: string;
  amount: number;
  billingDate: string;
  billingRef: string;
  matched?: boolean;
}
interface ReconciliationTableProps {
  systemTickets: TicketData[];
  billingEntries: BillingData[];
  onManualMatch: (systemTicketId: number, billingEntryId: number) => void;
  onUnmatch: (systemTicketId: number) => void;
}
const ReconciliationTable: React.FC<ReconciliationTableProps> = ({
  systemTickets,
  billingEntries,
  onManualMatch,
  onUnmatch
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null);
  const [selectedBilling, setSelectedBilling] = useState<number | null>(null);
  // Filter tickets and billing entries based on search term
  const filteredTickets = systemTickets.filter(ticket => ticket.ticketNumber.includes(searchTerm) || ticket.paxName.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredBillingEntries = billingEntries.filter(entry => entry.ticketNumber.includes(searchTerm));
  // Get the matching billing entry for a system ticket if it exists
  const getMatchingBillingEntry = (ticketNumber: string) => {
    return billingEntries.find(entry => entry.ticketNumber === ticketNumber && entry.matched);
  };
  // Get the matching system ticket for a billing entry if it exists
  const getMatchingSystemTicket = (ticketNumber: string) => {
    return systemTickets.find(ticket => ticket.ticketNumber === ticketNumber && ticket.reconciled);
  };
  // Handle selecting a system ticket for manual matching
  const handleSelectTicket = (id: number) => {
    setSelectedTicket(id === selectedTicket ? null : id);
    if (selectedBilling !== null && id !== selectedTicket) {
      // If we already have a billing entry selected, perform the match
      onManualMatch(id, selectedBilling);
      setSelectedTicket(null);
      setSelectedBilling(null);
    }
  };
  // Handle selecting a billing entry for manual matching
  const handleSelectBilling = (id: number) => {
    setSelectedBilling(id === selectedBilling ? null : id);
    if (selectedTicket !== null && id !== selectedBilling) {
      // If we already have a system ticket selected, perform the match
      onManualMatch(selectedTicket, id);
      setSelectedTicket(null);
      setSelectedBilling(null);
    }
  };
  // Determine the reconciliation status for a system ticket
  const getSystemTicketStatus = (ticket: TicketData) => {
    const matchingEntry = getMatchingBillingEntry(ticket.ticketNumber);
    if (!matchingEntry) {
      return 'unmatched';
    }
    if (Math.abs(ticket.totalCost - matchingEntry.amount) < 0.01) {
      return 'matched';
    }
    return 'mismatch';
  };
  // Determine the reconciliation status for a billing entry
  const getBillingEntryStatus = (entry: BillingData) => {
    const matchingTicket = getMatchingSystemTicket(entry.ticketNumber);
    if (!matchingTicket) {
      return 'unmatched';
    }
    if (Math.abs(entry.amount - matchingTicket.totalCost) < 0.01) {
      return 'matched';
    }
    return 'mismatch';
  };
  return <div className="space-y-4" data-id="element-638">
      <div className="flex flex-wrap items-center gap-4 mb-6" data-id="element-639">
        <div className="relative flex-1 min-w-[240px]" data-id="element-640">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-641">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-642" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by ticket number or passenger name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-643" />
        </div>
        <div className="flex items-center space-x-4" data-id="element-644">
          <div className="flex items-center" data-id="element-645">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-646"></span>
            <span className="text-sm text-gray-600" data-id="element-647">Matched</span>
          </div>
          <div className="flex items-center" data-id="element-648">
            <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-2" data-id="element-649"></span>
            <span className="text-sm text-gray-600" data-id="element-650">Mismatched</span>
          </div>
          <div className="flex items-center" data-id="element-651">
            <span className="inline-block w-3 h-3 rounded-full bg-gray-300 mr-2" data-id="element-652"></span>
            <span className="text-sm text-gray-600" data-id="element-653">Unmatched</span>
          </div>
        </div>
      </div>
      {selectedTicket !== null && selectedBilling === null && <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 flex items-center" data-id="element-654">
          <InfoIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-655" />
          <p className="text-sm text-blue-700" data-id="element-656">
            Select a billing entry to match with the selected system ticket, or
            click the system ticket again to deselect.
          </p>
        </div>}
      {selectedBilling !== null && selectedTicket === null && <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 flex items-center" data-id="element-657">
          <InfoIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-658" />
          <p className="text-sm text-blue-700" data-id="element-659">
            Select a system ticket to match with the selected billing entry, or
            click the billing entry again to deselect.
          </p>
        </div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-660">
        {/* System Tickets Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-661">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-662">
            <h3 className="text-sm font-medium text-gray-700" data-id="element-663">
              System Tickets
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-664">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-665">
              <thead className="bg-gray-50" data-id="element-666">
                <tr data-id="element-667">
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-668">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-669">
                    Ticket Number
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-670">
                    Passenger
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-671">
                    Amount
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-672">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-673">
                {filteredTickets.map(ticket => {
                const status = getSystemTicketStatus(ticket);
                const matchingEntry = getMatchingBillingEntry(ticket.ticketNumber);
                return <tr key={ticket.id} className={`hover:bg-blue-50 ${selectedTicket === ticket.id ? 'bg-blue-100' : ''}`} data-id="element-674">
                      <td className="px-3 py-4 whitespace-nowrap" data-id="element-675">
                        {status === 'matched' && <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-676" />}
                        {status === 'mismatch' && <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-677" />}
                        {status === 'unmatched' && <HelpCircleIcon className="h-5 w-5 text-gray-400" data-id="element-678" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-679">
                        {ticket.ticketNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-680">
                        {ticket.paxName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-681">
                        ${ticket.totalCost.toFixed(2)}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center text-sm" data-id="element-682">
                        {ticket.reconciled ? <button onClick={() => onUnmatch(ticket.id)} className="text-red-600 hover:text-red-800" data-id="element-683">
                            Unmatch
                          </button> : <button onClick={() => handleSelectTicket(ticket.id)} className="text-blue-600 hover:text-blue-800" data-id="element-684">
                            {selectedTicket === ticket.id ? 'Cancel' : 'Match'}
                          </button>}
                      </td>
                    </tr>;
              })}
                {filteredTickets.length === 0 && <tr data-id="element-685">
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-686">
                      No system tickets found.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
        {/* Billing Entries Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-687">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-688">
            <h3 className="text-sm font-medium text-gray-700" data-id="element-689">
              Billing Entries
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-690">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-691">
              <thead className="bg-gray-50" data-id="element-692">
                <tr data-id="element-693">
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-694">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-695">
                    Ticket Number
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-696">
                    Billing Ref
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-697">
                    Amount
                  </th>
                  <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-698">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-699">
                {filteredBillingEntries.map(entry => {
                const status = getBillingEntryStatus(entry);
                const matchingTicket = getMatchingSystemTicket(entry.ticketNumber);
                return <tr key={entry.id} className={`hover:bg-blue-50 ${selectedBilling === entry.id ? 'bg-blue-100' : ''}`} data-id="element-700">
                      <td className="px-3 py-4 whitespace-nowrap" data-id="element-701">
                        {status === 'matched' && <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-702" />}
                        {status === 'mismatch' && <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-703" />}
                        {status === 'unmatched' && <HelpCircleIcon className="h-5 w-5 text-gray-400" data-id="element-704" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-705">
                        {entry.ticketNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-706">
                        {entry.billingRef}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-707">
                        ${entry.amount.toFixed(2)}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center text-sm" data-id="element-708">
                        {entry.matched ? <button onClick={() => {
                      const matchingTicket = getMatchingSystemTicket(entry.ticketNumber);
                      if (matchingTicket) {
                        onUnmatch(matchingTicket.id);
                      }
                    }} className="text-red-600 hover:text-red-800" data-id="element-709">
                            Unmatch
                          </button> : <button onClick={() => handleSelectBilling(entry.id)} className="text-blue-600 hover:text-blue-800" data-id="element-710">
                            {selectedBilling === entry.id ? 'Cancel' : 'Match'}
                          </button>}
                      </td>
                    </tr>;
              })}
                {filteredBillingEntries.length === 0 && <tr data-id="element-711">
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-712">
                      No billing entries found.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
};
// Helper component for the info icon
const InfoIcon = ({
  className
}: {
  className?: string;
}) => {
  return <HelpCircleIcon className={className} data-id="element-713" />;
};
export default ReconciliationTable;